import React, { useMemo } from 'react';
import styled from 'styled-components';
import { AccordionSection, SplitChecksToggleBreakdown, Tag, ToggleableBreakdown } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { ICalculatorRules, ITipCalculatorParticipant, ITipCalculatorResultParticipant } from 'types';
import { buildParticipantSplitCheckBreakdown, centsToDollarsDisplay } from 'utils';

export interface IParticipantListItemProps {
  dataCy?: string;
  participant: ITipCalculatorParticipant;
  calculatorRules: ICalculatorRules;
}

const Layout = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  &:not(:last-child) {
    margin-bottom: ${toRem(theme.space.lg)};
  }
`;

const StyledAccordion = styled(AccordionSection)`
  h3 {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    font-weight: ${theme.font.weight.medium};
  }

  .participant-list-item-content {
    background: ${theme.color.neutral[50]};
    padding: 0;
  }
`;

const ParticipantContent = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: stretch;
  padding: 0 ${toRem(theme.space.lg)};
  overflow: hidden;
  width: 100%;
`;

const Breakdowns = styled.div`
  display: flex;
  flex-flow: column;

  > div:last-child {
    margin-bottom: ${toRem(theme.space.lg)};
  }
`;

const StyledTag = styled(Tag)`
  white-space: nowrap;
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  background-color: ${theme.color.primary[50]};
`;

const StyledToggleableBreakdown = styled(ToggleableBreakdown)`
  &:not(:last-child) {
    margin-bottom: ${toRem(theme.space.lg)};
  }
`;

const CalculatedTotal = styled.p`
  color: ${theme.color.neutral[800]};
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};
`;

const ParticipantListItem: React.FC<IParticipantListItemProps> = ({ dataCy = 'participant-list-item', participant, calculatorRules }) => {
  const renderSharedBreakdown = useMemo(
    () =>
      participant.calculations.map((calculation: ITipCalculatorResultParticipant) => (
        <StyledToggleableBreakdown
          calculatorRules={calculatorRules.rules}
          categoryGroups={calculatorRules.categoryGroups}
          calculation={{ ...calculation, job: { title: participant.name }, totals: { ...calculation.totals, totalMinWorked: 0 } }}
          key={`${calculation.name}_${calculation.segmentId}`}
          segmentName={calculation.name}
          disabled
        />
      )),
    [participant]
  );

  const renderOverrideBreakdown = useMemo(() => {
    const splitChecks = buildParticipantSplitCheckBreakdown(participant?.calculations);

    if (splitChecks?.received.length <= 0 && splitChecks?.contributions.length <= 0) return <></>;
    return <SplitChecksToggleBreakdown splitChecks={splitChecks} disabled />;
  }, [participant]);

  return (
    <Layout key={`participant-${participant.name}-${participant.type}`}>
      <StyledAccordion
        className="participant-list-item"
        dataCy={dataCy}
        title={participant?.name}
        subtitle={<CalculatedTotal data-cy="calculated-tips-total">{centsToDollarsDisplay(participant.gross)}</CalculatedTotal>}
        actions={<StyledTag appearance={Appearance.PRIMARY} label={participant.type} dataCy="participant-type" />}
        open={false}
        compact
      >
        <ParticipantContent data-cy={`${dataCy}-bottom-row`}>
          <Breakdowns>
            {renderSharedBreakdown}
            {renderOverrideBreakdown}
          </Breakdowns>
        </ParticipantContent>
      </StyledAccordion>
    </Layout>
  );
};

export default ParticipantListItem;
